/**
 * useUnsavedChangesGuard (TMMPDM-0036, FRM-008 / UI-009) — an editor page with changes asks before
 * they are lost. In-app navigation (a nav link, a breadcrumb, Back, Cancel) is held by the router's
 * `useBlocker` and asks "Leave without saving?" (Leave / Stay); closing or reloading the tab gets
 * the browser's own `beforeunload` prompt.
 *
 * A clean form never asks. `leave(to)` is the way out after a successful save: it releases the
 * guard first, so the page's own return navigation (and anything after it) is not held.
 *
 * TMMPDM-0044: `leaveAfterSave(to)` is that way out for a save that may land late. It returns with
 * `replace`, so Back goes past the editor rather than reopening it (NAV-011), and only while the
 * editor is still mounted on its own location — a save that answers after the user left shows its
 * toast and leaves them where they are (FRM-012).
 *
 * Needs a data router (`createBrowserRouter` / `createMemoryRouter`) — `useBlocker` throws in a
 * plain `<MemoryRouter>`.
 *
 * TMMPDM-0065 (TMM-B5): `useDiscardChangesGuard` is the same guard for an editing dialog — one with
 * more than two fields. Esc, the backdrop and Cancel ask the same Leave / Stay question when the
 * form is dirty, and close at once when it is clean.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlocker, useLocation, useNavigate, type NavigateOptions } from 'react-router-dom';
import { ConfirmDialog } from '@/components/page/ConfirmDialog';
import { useCopy } from './useCopy';

export interface UnsavedChangesGuard {
  /** The Leave / Stay confirmation; render it once on the page. */
  dialog: ReactNode;
  /** Navigate without asking (after a successful save). */
  leave: (to: string, options?: NavigateOptions) => void;
  /** After a save that may land late: return with `replace`, unless the user has left. */
  leaveAfterSave: (to: string) => void;
}

export interface DiscardChangesGuard {
  /** The Leave / Stay confirmation; render it once beside the dialog. */
  dialog: ReactNode;
  /** The dialog's `onClose` and its Cancel button: asks first when the form is dirty. */
  requestClose: () => void;
  /** The form reports its dirty state here (from an effect); the guard reads it on close. */
  setDirty: (dirty: boolean) => void;
}

/**
 * For the component that owns the `Dialog`: pass `requestClose` to `onClose` and Cancel, and hand
 * `setDirty` to the form, which keeps it current. `name` names the record in the prompt.
 */
export function useDiscardChangesGuard(name: string, onClose: () => void): DiscardChangesGuard {
  const { t } = useCopy();
  const dirtyRef = useRef(false);
  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);
  const [asking, setAsking] = useState(false);
  const dialog = (
    <ConfirmDialog
      open={asking}
      title={t('components.unsaved.title', 'Leave without saving?')}
      consequence={t('components.unsaved.consequence', 'Your changes to {{name}} will be lost.', {
        name,
      })}
      confirmLabel={t('components.unsaved.leave', 'Leave')}
      cancelLabel={t('components.unsaved.stay', 'Stay')}
      destructive
      onConfirm={() => {
        setAsking(false);
        dirtyRef.current = false;
        onClose();
      }}
      onCancel={() => setAsking(false)}
    />
  );
  return {
    dialog,
    setDirty,
    requestClose: () => {
      if (dirtyRef.current) setAsking(true);
      else onClose();
    },
  };
}

/** `name` names the record in the prompt ("Your changes to Launch USD will be lost."). */
export function useUnsavedChangesGuard(dirty: boolean, name: string): UnsavedChangesGuard {
  const { t } = useCopy();
  const navigate = useNavigate();
  const released = useRef(false);
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  // The editor's own location, and whether it is still mounted there.
  const own = useRef(here);
  const current = useRef(here);
  const mounted = useRef(false);
  useEffect(() => {
    current.current = here;
  }, [here]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !released.current &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (released.current) return;
      event.preventDefault();
      // Legacy browsers only show the prompt when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const dialog = (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      title={t('components.unsaved.title', 'Leave without saving?')}
      consequence={t('components.unsaved.consequence', 'Your changes to {{name}} will be lost.', {
        name,
      })}
      confirmLabel={t('components.unsaved.leave', 'Leave')}
      cancelLabel={t('components.unsaved.stay', 'Stay')}
      destructive
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  );

  return {
    dialog,
    leave: (to, options) => {
      released.current = true;
      void navigate(to, options);
    },
    leaveAfterSave: (to) => {
      if (!mounted.current || current.current !== own.current) return;
      released.current = true;
      void navigate(to, { replace: true });
    },
  };
}
